import { useTranslation } from 'react-i18next';
import { 
  Sheet, 
  SheetContent, 
  SheetDescription, 
  SheetHeader, 
  SheetTitle 
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useLearning } from '@/contexts/LearningContext';
import { useRole } from '@/contexts/RoleContext';
import { 
  getContentForRole, 
  categoryLabelKeys, 
  categoryColors 
} from '@/data/learningContent';
import { 
  BookOpen, 
  Target, 
  Lightbulb, 
  TrendingUp, 
  CheckCircle2,
  ExternalLink,
  Award,
  User,
  Calculator
} from 'lucide-react';
import { Link } from 'react-router-dom';

export function MetricLearningDrawer() {
  const { t } = useTranslation();
  const { isDrawerOpen, activeMetric, closeDrawer } = useLearning();
  const { currentRole } = useRole();

  if (!activeMetric) return null;

  const roleContent = getContentForRole(activeMetric, currentRole);

  const getRoleLabel = () => {
    switch (currentRole) {
      case 'executive':
        return t('learn.forExecutives');
      case 'manager':
        return t('learn.forManagers');
      case 'rep':
        return t('learn.forSalesReps');
      default:
        return '';
    }
  };

  return (
    <Sheet open={isDrawerOpen} onOpenChange={(open) => !open && closeDrawer()}>
      <SheetContent className="w-full sm:max-w-lg overflow-hidden">
        <ScrollArea className="h-full pr-4">
          <SheetHeader className="pb-4">
            <div className="flex items-center gap-2 mb-2">
              <Badge className={categoryColors[activeMetric.category]}>
                {t(categoryLabelKeys[activeMetric.category])}
              </Badge>
              {activeMetric.shortName && (
                <Badge variant="outline">{activeMetric.shortName}</Badge>
              )}
            </div>
            <SheetTitle className="text-xl">{t(activeMetric.nameKey)}</SheetTitle>
            {activeMetric.formulaKey && (
              <SheetDescription className="flex items-center gap-2 text-xs font-mono bg-muted px-2 py-1 rounded">
                <Calculator className="h-3 w-3" />
                {t(activeMetric.formulaKey)}
              </SheetDescription>
            )}
          </SheetHeader>

          <div className="space-y-6 pb-8">
            {/* 1. What is it? */}
            <section>
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="h-4 w-4 text-primary" />
                <h3 className="font-semibold text-sm">{t('learning.whatIsThisMetric')}</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t(activeMetric.whatIsItKey)}
              </p>
            </section>

            <Separator />

            {/* 2. Why it matters */}
            <section>
              <div className="flex items-center gap-2 mb-2">
                <Target className="h-4 w-4 text-primary" />
                <h3 className="font-semibold text-sm">{t('learning.whyItMatters')}</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t(activeMetric.whyItMattersKey)}
              </p>
            </section>

            <Separator />

            {/* 3. WBD View */}
            <section>
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="h-4 w-4 text-yellow-500" />
                <h3 className="font-semibold text-sm">{t('learning.wbdPerspective')}</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t(activeMetric.wbdViewKey)}
              </p>
            </section>

            <Separator />

            {/* 4. Benchmarks */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Award className="h-4 w-4 text-primary" />
                <h3 className="font-semibold text-sm">{t('learning.whatIsGoodVsBad')}</h3>
              </div>
              <div className="space-y-2">
                <div className="flex items-start gap-3 p-2 rounded bg-green-50 dark:bg-green-900/20">
                  <Badge variant="outline" className="bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400 shrink-0">
                    {t('learning.good')}
                  </Badge>
                  <span className="text-sm">{t(activeMetric.benchmarks.good)}</span>
                </div>
                <div className="flex items-start gap-3 p-2 rounded bg-yellow-50 dark:bg-yellow-900/20">
                  <Badge variant="outline" className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-400 shrink-0">
                    {t('learning.avg')}
                  </Badge>
                  <span className="text-sm">{t(activeMetric.benchmarks.average)}</span>
                </div>
                <div className="flex items-start gap-3 p-2 rounded bg-red-50 dark:bg-red-900/20">
                  <Badge variant="outline" className="bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400 shrink-0">
                    {t('learning.poor')}
                  </Badge>
                  <span className="text-sm">{t(activeMetric.benchmarks.poor)}</span>
                </div>
              </div>
            </section>

            <Separator />

            {/* 5. How to improve */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="h-4 w-4 text-primary" />
                <h3 className="font-semibold text-sm">{t('learning.howToImprove')}</h3>
              </div>
              <ul className="space-y-2">
                {activeMetric.howToImproveKeys.map((tipKey, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                    <span className="text-muted-foreground">{t(tipKey)}</span>
                  </li>
                ))}
              </ul>
            </section>

            <Separator />

            {/* Role-specific guidance */}
            <section className="bg-primary/5 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4 text-primary" />
                <h3 className="font-semibold text-sm capitalize">
                  {getRoleLabel()}
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                {t(roleContent.contextKey)}
              </p>
              <p className="text-sm font-medium text-primary">
                💡 {t(roleContent.focusKey)}
              </p>
            </section>

            {/* Related Links */}
            {(activeMetric.relatedDashboards.length > 0 || activeMetric.relatedPlaybooks?.length) && (
              <>
                <Separator />
                <section>
                  <h3 className="font-semibold text-sm mb-3">{t('learning.related')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {activeMetric.relatedDashboards.map((dashboard) => (
                      <Button 
                        key={dashboard} 
                        variant="outline" 
                        size="sm" 
                        asChild
                        className="gap-1"
                      >
                        <Link to={dashboard} onClick={closeDrawer}>
                          <ExternalLink className="h-3 w-3" />
                          {dashboard.replace('/', '').replace('-', ' ')}
                        </Link>
                      </Button>
                    ))}
                    {activeMetric.relatedPlaybooks?.map((playbook) => (
                      <Button 
                        key={playbook} 
                        variant="secondary" 
                        size="sm" 
                        asChild
                        className="gap-1"
                      >
                        <Link to="/playbooks" onClick={closeDrawer}>
                          <BookOpen className="h-3 w-3" />
                          {playbook}
                        </Link>
                      </Button>
                    ))}
                  </div>
                </section>
              </>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
